import { useState, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  SkeletonBodyText,
  Modal,
  TextField,
  Divider,
  Box,
} from '@shopify/polaris';
import {
  useWebhookConfig,
  useConfigureWebhook,
  useToggleWebhook,
  useTestWebhook,
  useDisconnectWebhook,
} from '../hooks/api';
import { GateBanner } from '../components/GateBanner';
import { useLanguage, useTranslation } from '../i18n/LanguageContext';
import { localeMap } from '../i18n/translations';

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'connected':
      return <Badge tone="success">Connected</Badge>;
    case 'warning':
      return <Badge tone="warning">Warning</Badge>;
    case 'error':
      return <Badge tone="critical">Error</Badge>;
    case 'disabled':
      return <Badge>Disabled</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
}

export function Webhooks() {
  // The whole /webhooks-config route is behind requireFeature('webhooks_out'),
  // which only PRO has. This page used to drop the query error on the floor, so
  // a GROWTH merchant got the full "Configure Webhook" call to action for a
  // feature their plan cannot use, and no explanation anywhere. Verified live on
  // 2026-07-27: the API answered 403 FEATURE_NOT_AVAILABLE / requiredPlan PRO
  // while the screen kept inviting them to configure it.
  const { data: config, isLoading, error: loadError } = useWebhookConfig();
  const gateCode = (loadError as { code?: string } | null)?.code;
  const isGated = gateCode === 'FEATURE_NOT_AVAILABLE' || gateCode === 'BILLING_REQUIRED';
  const configureMutation = useConfigureWebhook();
  const toggleMutation = useToggleWebhook();
  const testMutation = useTestWebhook();
  const disconnectMutation = useDisconnectWebhook();
  const { language } = useLanguage();
  const t = useTranslation();

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ success?: boolean; message?: string } | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleConfigure = useCallback(async () => {
    setConfigError(null);
    if (!webhookUrl) {
      setConfigError('Webhook URL is required.');
      return;
    }

    try {
      new URL(webhookUrl);
    } catch {
      setConfigError('Invalid URL format.');
      return;
    }

    try {
      const result = await configureMutation.mutateAsync({ url: webhookUrl });
      setWebhookSecret(result.secret);
      setShowConfigModal(false);
      setWebhookUrl('');
    } catch (error: any) {
      setConfigError(error.message || t.common.configFailed);
    }
  }, [webhookUrl, configureMutation]);

  const handleToggle = useCallback(
    async (enabled: boolean) => {
      setActionError(null);
      try {
        await toggleMutation.mutateAsync(enabled);
      } catch (error: any) {
        console.error('Failed to toggle:', error);
        setActionError(error.message || t.common.actionFailed);
      }
    },
    [toggleMutation]
  );

  const handleTest = useCallback(async () => {
    setTestResult(null);
    setActionError(null);
    try {
      const result: any = await testMutation.mutateAsync();
      setTestResult({
        success: true,
        message: result.message || 'Test webhook sent successfully.',
      });
    } catch (error: any) {
      setTestResult({ success: false, message: error.message || 'Test webhook failed.' });
    }
  }, [testMutation]);

  const handleDisconnect = useCallback(async () => {
    setActionError(null);
    try {
      await disconnectMutation.mutateAsync();
      setShowDisconnectModal(false);
      setWebhookSecret(null);
    } catch (error: any) {
      console.error('Failed to disconnect:', error);
      setActionError(error.message || t.common.actionFailed);
    }
  }, [disconnectMutation]);

  if (isLoading) {
    return (
      <Page title="Outbound Webhooks">
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={6} />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Outbound Webhooks"
      subtitle="Receive conversion data on your own server via HTTP POST"
      primaryAction={
        config?.connected
          ? {
              content: config.isEnabled ? 'Disable Webhook' : 'Enable Webhook',
              onAction: () => handleToggle(!config.isEnabled),
              loading: toggleMutation.isPending,
            }
          : undefined
      }
    >
      <Layout>
        {/* Plan gate / load failure */}
        {loadError && (
          <Layout.Section>
            <GateBanner error={loadError as Error} />
          </Layout.Section>
        )}

        {/* Action Error Banner */}
        {actionError && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setActionError(null)}>
              <p>{actionError}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Webhook Secret Banner */}
        {webhookSecret && (
          <Layout.Section>
            <Banner
              tone="warning"
              title="Save your webhook secret"
              onDismiss={() => setWebhookSecret(null)}
            >
              <BlockStack gap="200">
                <Text as="p" variant="bodyMd">
                  This secret is used to verify webhook signatures. Copy it now — it will not be shown again.
                </Text>
                <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                  <Text as="p" variant="bodyMd" fontWeight="bold">
                    {webhookSecret}
                  </Text>
                </Box>
              </BlockStack>
            </Banner>
          </Layout.Section>
        )}

        {/* Error Banner */}
        {config?.lastErrorMessage && (
          <Layout.Section>
            <Banner tone="warning" title="Delivery Issue">
              <BlockStack gap="100">
                <Text as="p" variant="bodyMd">
                  {config.lastErrorMessage}
                </Text>
                {config.consecutiveErrors && config.consecutiveErrors > 0 && (
                  <Text as="p" variant="bodyMd" tone="subdued">
                    {config.consecutiveErrors} consecutive error{config.consecutiveErrors !== 1 ? 's' : ''}
                  </Text>
                )}
              </BlockStack>
            </Banner>
          </Layout.Section>
        )}

        {/* Test Result */}
        {testResult && (
          <Layout.Section>
            <Banner
              tone={testResult.success ? 'success' : 'critical'}
              onDismiss={() => setTestResult(null)}
            >
              <p>{testResult.message}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Connection Status */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  Webhook Endpoint
                </Text>
                {config?.connected && <StatusBadge status={config.status || 'connected'} />}
              </InlineStack>

              {config?.connected ? (
                <BlockStack gap="300">
                  <InlineStack gap="200">
                    <Text as="span" variant="bodyMd" tone="subdued">
                      URL:
                    </Text>
                    <Text as="span" variant="bodyMd" fontWeight="semibold">
                      {config.url}
                    </Text>
                  </InlineStack>

                  {config.lastSentAt && (
                    <InlineStack gap="200">
                      <Text as="span" variant="bodyMd" tone="subdued">
                        Last Sent:
                      </Text>
                      <Text as="span" variant="bodyMd">
                        {new Date(config.lastSentAt).toLocaleString(localeMap[language])}
                      </Text>
                    </InlineStack>
                  )}

                  {config.createdAt && (
                    <InlineStack gap="200">
                      <Text as="span" variant="bodyMd" tone="subdued">
                        Created:
                      </Text>
                      <Text as="span" variant="bodyMd">
                        {new Date(config.createdAt).toLocaleString(localeMap[language])}
                      </Text>
                    </InlineStack>
                  )}

                  <Divider />

                  <InlineStack gap="200">
                    <Button onClick={handleTest} loading={testMutation.isPending}>
                      Send Test Webhook
                    </Button>
                    <Button onClick={() => setShowConfigModal(true)}>
                      Update Configuration
                    </Button>
                    <Button
                      variant="plain"
                      tone="critical"
                      onClick={() => setShowDisconnectModal(true)}
                    >
                      Disconnect
                    </Button>
                  </InlineStack>
                </BlockStack>
              ) : (
                <BlockStack gap="300">
                  <Text as="p" variant="bodyMd">
                    Configure a webhook endpoint to receive real-time conversion data via HTTP POST.
                    Each payload is signed with HMAC-SHA256 so you can verify its authenticity.
                  </Text>
                  {/* On a gated plan the configure call would fail on save, so
                      the banner above is the only honest action to offer. */}
                  {!isGated && (
                    <Box>
                      <Button variant="primary" onClick={() => setShowConfigModal(true)}>
                        Configure Webhook
                      </Button>
                    </Box>
                  )}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Payload Documentation */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Payload Format
              </Text>
              <Text as="p" variant="bodyMd">
                When a conversion is tracked, Trackful sends a POST request to your URL with the following JSON body:
              </Text>
              <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                <Text as="p" variant="bodyMd">
                  {'{'} "event": "conversion.tracked", "timestamp": "...", "data": {'{'} "orderId": "...", "orderName": "#1001", "revenue": 99.99, "currency": "EUR", "source": "WEBHOOK", "googleAdsTracked": true, "metaTracked": true, ... {'}'} {'}'}
                </Text>
              </Box>
              <BlockStack gap="200">
                <Text as="p" variant="bodyMd" fontWeight="semibold">
                  Signature Verification
                </Text>
                <Text as="p" variant="bodyMd">
                  The request includes an <code>X-Trackful-Signature</code> header containing an HMAC-SHA256
                  hex digest of the raw request body, using your webhook secret as the key.
                </Text>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Configuration Modal */}
        <Modal
          open={showConfigModal}
          onClose={() => {
            setShowConfigModal(false);
            setConfigError(null);
          }}
          title="Configure Outbound Webhook"
          primaryAction={{
            content: 'Save Configuration',
            onAction: handleConfigure,
            loading: configureMutation.isPending,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => {
                setShowConfigModal(false);
                setConfigError(null);
              },
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="400">
              {configError && (
                <Banner tone="critical">
                  <p>{configError}</p>
                </Banner>
              )}

              <TextField
                label="Webhook URL"
                value={webhookUrl}
                onChange={setWebhookUrl}
                placeholder="https://your-server.com/webhooks/trackful"
                autoComplete="off"
                helpText="The URL that will receive conversion data via POST. HTTPS is recommended."
              />

              <Banner tone="info">
                <Text as="p" variant="bodyMd">
                  A new signing secret will be generated. Make sure to save it after configuration.
                </Text>
              </Banner>
            </BlockStack>
          </Modal.Section>
        </Modal>

        {/* Disconnect Confirmation Modal */}
        <Modal
          open={showDisconnectModal}
          onClose={() => setShowDisconnectModal(false)}
          title="Disconnect Webhook"
          primaryAction={{
            content: 'Disconnect',
            onAction: handleDisconnect,
            loading: disconnectMutation.isPending,
            destructive: true,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setShowDisconnectModal(false),
            },
          ]}
        >
          <Modal.Section>
            <Text as="p" variant="bodyMd">
              Are you sure you want to disconnect the outbound webhook? Conversion data will no longer
              be sent to your endpoint, and the signing secret will be permanently deleted.
            </Text>
          </Modal.Section>
        </Modal>
      </Layout>
    </Page>
  );
}
